'use es6';

import { Component } from 'react';
import PropTypes from 'prop-types';
import FormattedMessage from 'I18n/components/FormattedMessage';
import text from 'I18n/utils/unescapedText';
import { bindAll } from 'underscore';
import shallowCompare from 'react-addons-shallow-compare';
import { MENU_TREE_ADD_CHILD, MENU_TREE_ADD_NODE, MENU_TREE_CLONE_NODE, MENU_NODE_REPLACE } from 'simple-menus-core/actions/ActionTypes';
import withConnectedMenuOptions from 'simple-menus-lib/components/menu/decorators/withConnectedMenuOptions';
import withDeleteMenuNodeModal from 'simple-menus-lib/components/menu/decorators/withDeleteMenuNodeModal';
import MenuNodeTypes from 'simple-menus-core/enums/MenuNodeTypes';
import UITextInput from 'UIComponents/input/UITextInput';
import UIIconButton from 'UIComponents/button/UIIconButton';
import UIIcon from 'UIComponents/icon/UIIcon';
import UIDropdown from 'UIComponents/dropdown/UIDropdown';
import UIButton from 'UIComponents/button/UIButton';
import UIList from 'UIComponents/list/UIList';
import PagesLinkSelect from 'simple-menus-lib/components/pages/PagesLinkSelect';
import UICheckbox from 'UIComponents/input/UICheckbox';
import HR from 'UIComponents/elements/HR';
import { compose } from 'redux';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const i18nKey = key => `simpleMenusLib.components.menuItemOptions.${key}`;
const i18nActionsKey = key => `${i18nKey('actions')}.${key}`;
const i18nOptionsKey = (type, key) => `${i18nKey('options')}.${type}.${key}`;
const PRIMARY_ACTIONS = [MENU_TREE_ADD_CHILD, MENU_TREE_ADD_NODE, MENU_TREE_CLONE_NODE];
const SWAP_ACTIONS = [MenuNodeTypes.NO_LINK, MenuNodeTypes.PAGE_LINK, MenuNodeTypes.PAGE_LINK_WITH_PARAMS, MenuNodeTypes.URL_LINK];
class MenuItemOptions extends Component {
  constructor(props) {
    super(props);
    bindAll(this, ['onExternalChange', 'onMouseEnter', 'onMouseLeave', 'onOpenChange', 'onPageLinkIdChange']);
    this.state = {
      isHovering: false,
      isOpen: false
    };
  }
  shouldComponentUpdate(nextProps, nextState) {
    /* Don't update when deleting node so flip/move animates */
    if (!nextProps.id || !nextProps.type) return false;
    return shallowCompare(this, nextProps, nextState);
  }
  onExternalChange({
    target: {
      checked
    }
  }) {
    this.props.setLinkExternal(checked);
  }
  onMouseEnter() {
    if (this.props.readOnly) return;
    this.setState({
      isHovering: true
    });
  }
  onMouseLeave() {
    this.setState({
      isHovering: false
    });
  }
  onOpenChange({
    target: {
      value: isOpen
    }
  }) {
    /* Allow dropdown to trigger onChange before switching */
    setTimeout(() => this.setState({
      isOpen
    }), 0);
  }
  onPageLinkIdChange(evt) {
    this.props.onPageLinkIdChange(evt.target.value);
  }
  renderEditorButton() {
    const {
      editorUrl
    } = this.props;
    if (!editorUrl) return null;
    return /*#__PURE__*/_jsx(UIButton, {
      href: editorUrl,
      external: true,
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: i18nActionsKey('openInEditor.label')
      })
    });
  }
  renderActionsOptions() {
    const {
      type,
      addChild,
      addNodeBelow,
      cloneNode,
      replaceNode,
      isExternal
    } = this.props;
    const primaryHandlers = {
      [MENU_TREE_ADD_CHILD]: addChild,
      [MENU_TREE_ADD_NODE]: addNodeBelow,
      [MENU_TREE_CLONE_NODE]: cloneNode
    };
    const secondaryHandlers = {
      [MenuNodeTypes.NO_LINK]: () => replaceNode(MenuNodeTypes.NO_LINK),
      [MenuNodeTypes.PAGE_LINK]: () => replaceNode(MenuNodeTypes.PAGE_LINK),
      [MenuNodeTypes.PAGE_LINK_WITH_PARAMS]: () => replaceNode(MenuNodeTypes.PAGE_LINK_WITH_PARAMS),
      [MenuNodeTypes.URL_LINK]: () => replaceNode(MenuNodeTypes.URL_LINK)
    };
    const primaryButtons = PRIMARY_ACTIONS.map(action => {
      return /*#__PURE__*/_jsx(UIButton, {
        onClick: primaryHandlers[action],
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nActionsKey(`primaryOptions.${action}`)
        })
      }, action);
    });
    const secondaryButtons = SWAP_ACTIONS.filter(action => action !== type).map(action => {
      return /*#__PURE__*/_jsx(UIButton, {
        "data-test-id": action,
        className: "p-left-10",
        onClick: secondaryHandlers[action],
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nActionsKey(`swapOptions.${action}`)
        })
      }, action);
    });
    return /*#__PURE__*/_jsxs(UIList, {
      children: [primaryButtons, this.renderEditorButton(), /*#__PURE__*/_jsx(HR, {
        distance: "extra-small"
      }), /*#__PURE__*/_jsx("p", {
        className: "simple-menus-item-actions-swap-label",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nActionsKey(`primaryOptions.${MENU_NODE_REPLACE}`)
        })
      }), /*#__PURE__*/_jsx(UIList, {
        children: secondaryButtons
      }), /*#__PURE__*/_jsx(HR, {
        distance: "extra-small"
      }), /*#__PURE__*/_jsx(UICheckbox, {
        checked: isExternal,
        onChange: this.onExternalChange,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nActionsKey('external')
        })
      })]
    });
  }
  renderActions() {
    const {
      isHovering,
      isOpen
    } = this.state;
    if (!isHovering && !isOpen) return null;
    return /*#__PURE__*/_jsxs("div", {
      children: [/*#__PURE__*/_jsx(UIDropdown, {
        "data-test-id": "menu-item-action-button",
        buttonUse: "link",
        buttonText: text(i18nActionsKey('label')),
        onOpenChange: this.onOpenChange,
        children: this.renderActionsOptions()
      }), /*#__PURE__*/_jsx(UIIconButton, {
        use: "link",
        onClick: this.props.deleteNode,
        children: /*#__PURE__*/_jsx(UIIcon, {
          name: "delete"
        })
      })]
    });
  }
  renderNoLink() {
    return /*#__PURE__*/_jsx(UITextInput, {
      "data-test-id": MenuNodeTypes.NO_LINK,
      styled: false,
      disabled: true,
      placeholder: text(i18nOptionsKey(MenuNodeTypes.NO_LINK, 'placeholder'))
    }, "noLink");
  }
  renderUrlLink() {
    const {
      linkUrl
    } = this.props;
    return /*#__PURE__*/_jsx("div", {
      draggable: true,
      onDragStart: e => {
        // When trying to highlight text prevent the
        // entire component from dragging
        e.preventDefault();
        e.stopPropagation();
      },
      children: /*#__PURE__*/_jsx(UITextInput, {
        "data-test-id": "url-link-input",
        placeholder: text(i18nOptionsKey(MenuNodeTypes.URL_LINK, 'placeholder')),
        defaultValue: linkUrl,
        onChange: this.props.onLinkUrlChange,
        disabled: this.props.readOnly
      })
    }, "urlLink");
  }
  renderPageSelect() {
    const {
      editorUrl,
      isPublished,
      isVisible,
      pageLinkId,
      pageLinkName,
      readOnly
    } = this.props;
    const placeHolder = pageLinkName || text(i18nOptionsKey(MenuNodeTypes.PAGE_LINK, 'placeholder'));
    return /*#__PURE__*/_jsx(PagesLinkSelect, {
      editorUrl: editorUrl,
      isPublished: isPublished,
      isVisible: isVisible,
      onChange: this.onPageLinkIdChange,
      pageLinkId: pageLinkId,
      placeholder: placeHolder,
      readOnly: readOnly
    }, "pageSelect");
  }
  renderPageSelectWithUrlParams() {
    const {
      linkParams
    } = this.props;
    return /*#__PURE__*/_jsxs("div", {
      className: "page-select-with-url-params",
      draggable: true,
      onDragStart: e => {
        // When trying to highlight text prevent the
        // entire component from dragging
        e.preventDefault();
        e.stopPropagation();
      },
      children: [this.renderPageSelect(), /*#__PURE__*/_jsx(UITextInput, {
        "data-test-id": "page-link-with-params-input",
        placeholder: text(i18nOptionsKey(MenuNodeTypes.PAGE_LINK_WITH_PARAMS, 'placeholder')),
        defaultValue: linkParams,
        onChange: this.props.onLinkParamsChange,
        readOnly: this.props.readOnly
      })]
    }, "urlParams");
  }
  renderOptions() {
    const {
      type
    } = this.props;
    const options = [type === MenuNodeTypes.NO_LINK && this.renderNoLink(), type === MenuNodeTypes.URL_LINK && this.renderUrlLink(), type === MenuNodeTypes.PAGE_LINK && this.renderPageSelect(), type === MenuNodeTypes.PAGE_LINK_WITH_PARAMS && this.renderPageSelectWithUrlParams()].filter(Boolean);
    return /*#__PURE__*/_jsx("div", {
      className: "menu-options",
      children: options
    });
  }
  render() {
    return /*#__PURE__*/_jsxs("div", {
      className: "simple-menus-menu-item-options",
      "data-test-id": "simple-menu-action-options",
      onMouseEnter: this.onMouseEnter,
      onMouseLeave: this.onMouseLeave,
      children: [this.renderOptions(), this.renderActions(), this.props.deleteMenuNodeModal]
    });
  }
}
MenuItemOptions.propTypes = {
  addChild: PropTypes.func.isRequired,
  addNodeBelow: PropTypes.func.isRequired,
  cloneNode: PropTypes.func.isRequired,
  deleteMenuNodeModal: PropTypes.node,
  deleteNode: PropTypes.func.isRequired,
  editorUrl: PropTypes.string,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  isPublished: PropTypes.bool,
  isVisible: PropTypes.bool.isRequired,
  linkUrl: PropTypes.string,
  linkParams: PropTypes.string,
  isExternal: PropTypes.bool,
  onLinkParamsChange: PropTypes.func.isRequired,
  onLinkUrlChange: PropTypes.func.isRequired,
  onPageLinkIdChange: PropTypes.func.isRequired,
  pageLinkId: PropTypes.number,
  pageLinkName: PropTypes.string,
  readOnly: PropTypes.bool,
  replaceNode: PropTypes.func.isRequired,
  setLinkExternal: PropTypes.func.isRequired,
  type: PropTypes.oneOf(Object.keys(MenuNodeTypes))
};
export default compose(withConnectedMenuOptions(), withDeleteMenuNodeModal())(MenuItemOptions);